import React, { useEffect, useState, useMemo } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { envConfig, getPaypalCheckoutSessionAPI, confirmPaypalPaymentAPI } from "../../services/api";
import { useSelector } from "react-redux";
import { track } from "@plausible-analytics/tracker";
import { useMutation } from "react-query";
import { randomIdGenerator, v2PaymentRequestData } from "./helper";
import { useLocation } from "react-router-dom";

const PaypalPayment = ({ stateBundle, paymentResponse, guestData }) => {
  const { selectedCurrency } = useSelector(state => state.currency);
  const initialOptions = {
    "client-id": envConfig.REACT_APP_PAYPAL_CLIENT_ID,
    "currency": selectedCurrency,
  };
  const { userData } = useSelector((state) => state.auth);
  const country = useSelector(state=>state?.country?.selectedCountry?.name);
  const randomIdFromStore = useSelector((state) => state.auth.randomId);
  const randomId = useMemo(() => randomIdFromStore || randomIdGenerator(), [randomIdFromStore]);
  const { state } = useLocation();
  const [session, setSession] = useState(null);

  const { name, description, prices, price: originalBasePrice, speed, duration, dataAmount, iccid } = stateBundle ?? {};
  const price = prices?.[selectedCurrency] || originalBasePrice;

  const { mutate: createSession, isLoading: sessionLoading, error: sessionError } = useMutation(getPaypalCheckoutSessionAPI, {
    onSuccess: (res) => {
     // console.log("Full PayPal session response:", JSON.stringify(res, null, 2));
      setSession(res);
    },
    onError: (err) => {
      // console.error("PayPal session creation failed", err);
    }
  });

  const { mutate: confirmPayment, isLoading: confirmLoading } = useMutation(
    ({ orderID, randomId }) => confirmPaypalPaymentAPI(orderID, randomId),
    {
      onSuccess: (res) => {
        paymentResponse(res);
      },
      onError: (err) => {
        console.error("PayPal confirmation failed", err);
        paymentResponse({ status: "failed", error: err.message });
      }
    }
  );

  useEffect(() => {
    // console.log("Initializing PayPal session...", { stateBundle, userData, randomId });
    const customerDetails = {
      firstName: userData?.firstName || guestData?.firstName || "",
      lastName: userData?.lastName || guestData?.lastName || "",
      email: userData?.email || guestData?.email || "",
      phone: { callingCode: "", localPhoneNumber: "" }
    };

    const dataPlans = [{
      name, description, quantity: 1, price, speed, duration, dataAmount,
      promoCode: state?.promoCode || "" 
    }];

    const paymentData = {
      contactInfo: customerDetails,
      orderSummary: { dataPlans, totalOrderAmount: price, currency: selectedCurrency },
      paymentGateway: "Paypal",
      iccid: iccid ?? '',
      randomId: randomId,
      ...v2PaymentRequestData,
    };

    createSession(paymentData);
  }, [createSession, userData?.firstName, userData?.lastName, userData?.email, guestData?.firstName, guestData?.lastName, guestData?.email, name, description, price, speed, duration, dataAmount, iccid, stateBundle?.promoCode, selectedCurrency, randomId]);

  const onApprove = (data, actions) => {
    track("event-payment", { props: { country }})
    return actions.order.capture().then((e) => {
      if (session?.orderID) {
        confirmPayment({ orderID: session.orderID, randomId });
      }
    });
  };
  const onError = (data, actions) => {
    console.error("PayPal SDK error", data);
  };

  if (sessionLoading) {
    return <div className="paypal-loading text-center p-3 w-full">
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
        <p>Initializing PayPal...</p>
    </div>;
  }

  if (sessionError) {
    return <div className="paypal-error text-center p-3 w-full text-red-500">
        <p>Failed to initialize PayPal: {sessionError?.message}</p>
        <button onClick={() => window.location.reload()} className="p-button p-component mt-2">Retry</button>
    </div>;
  }

  return (
    <>
      <div className="col-12 md:col-12">
        <div className="input-wrapper paypal-section-button" style={{ pointerEvents: confirmLoading ? 'none' : 'auto', opacity: confirmLoading ? 0.7 : 1 }}>
          {session?.checkoutSessionID ? (
            <PayPalScriptProvider options={initialOptions}>
              <PayPalButtons
                fundingSource="paypal"
                style={{ layout: "vertical" }}
                createOrder={() => session.checkoutSessionID}
                onApprove={onApprove}
                onError={onError}
                onCancel={onError}
              />
            </PayPalScriptProvider>
          ) : (
             <div className="text-center p-3">
               <p>Unable to load PayPal session. Please try again.</p>
             </div>
          )}
        </div>
      </div>
      {confirmLoading && (
        <div className="flex align-items-center justify-content-center gap-2 mt-3 w-full">
             <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.2rem' }}></i>
             <span style={{ fontSize: '1.1rem' }}>Validating Payment...</span>
        </div>
      )}
    </>
  );
};

export default PaypalPayment;
